import {
  CheckCircleOutlined as SuccessIcon,
  ErrorOutlined as ErrorIcon,
  Preview as PreviewIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import orderBy from "lodash/orderBy";
import type { FC } from "react";
import { useState } from "react";
import CancelButton from "../../components/cancelButton/CancelButton";
import dayjs from "../../dayjs";
import type { EmailForOrder } from "../ordersApi";
import { useEmailsSentForOrder } from "./useEmailsSentForOrder";
import Email from "../../components/links/Email";

function deliveryStateToIcon(state: string | null) {
  switch (state) {
    case "SUCCESS":
      return <SuccessIcon color="success" />;
    case "ERROR":
      return <ErrorIcon color="error" />;
    case null:
      return null;
    default:
      return state;
  }
}

function typeToEmailName(email: EmailForOrder) {
  switch (email.type) {
    // ADVANCE_INVOICE_CREATED is the legacy type of the proforma notification. Until December 2024 the automation
    // issued advance invoices under the same type; no such records remain, only proformas.
    case "PROFORMA_INVOICE_CREATED":
    case "ADVANCE_INVOICE_CREATED":
      return "faktura proforma";
    case "ORDER_CONFIRMATION_NOT_ACCEPTED":
      return "potwierdzienie niezaakceptowane";
    case "ORDER_CANCELLED":
      return "zamówienie anulowane";
    case "PARTICIPANT_LIST":
      return "lista uczestników";
    case "ADVANCE_NOT_PAID":
      return "zaliczka niewpłacona";
    case "CONTACT_REQUIRED":
      return "kontakt wymagany";
    case "TO_BE_CANCELLED":
      return "zamówienie do anulacji";
    case "ORDER_CONFIRMATION_CREATED":
      return "potwierdzenie wysłane";
    case "ORDER_CONFIRMATION_UPDATED":
      return "potwierdzenie po zmianach wysłane";
    case "ORDER_CHECK_IN":
      return "odprawa wysłana";
    case "NEW_BOOKING":
      return `nowa rezerwacja ${email.provider}`;
    case "ORDER_CREATED":
      return "zamówienie utworzone przez klienta";
    default:
      return email.type;
  }
}

const EmailRow: FC<{ email: EmailForOrder }> = ({ email }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { subject, to, attachments, body } = email.email;
  return (
    <TableRow>
      <TableCell>{dayjs(email.createdAt).format("DD-MM-YYYY HH:mm")}</TableCell>
      <TableCell>{typeToEmailName(email)}</TableCell>
      <TableCell>{deliveryStateToIcon(email.deliveryState)}</TableCell>
      <TableCell>
        <IconButton onClick={() => setDialogOpen(true)} aria-label="Zobacz wiadomość">
          <Tooltip title="Zobacz wiadomość">
            <PreviewIcon />
          </Tooltip>
        </IconButton>
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xl" fullWidth>
          <DialogTitle>{subject}</DialogTitle>
          <DialogContent>
            <Stack direction="column" spacing={2}>
              <Typography variant="subtitle2">Odbiorcy</Typography>
              <Stack direction="row" spacing={1}>
                {to.map((e) => (
                  <Email key={e} value={e} />
                ))}
              </Stack>
              <Typography variant="subtitle2">Treść</Typography>
              <TextField fullWidth multiline aria-readonly value={body} />
              <Typography variant="subtitle2">Załączniki</Typography>
              {attachments.length > 0 ? (
                <ul>
                  {attachments.map((a, idx) => (
                    <li key={`${a}:${idx}`}>{a}</li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2">Brak</Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <CancelButton onClick={() => setDialogOpen(false)}>Zamknij podgląd</CancelButton>
          </DialogActions>
        </Dialog>
      </TableCell>
    </TableRow>
  );
};

const EmailsSentForOrder: FC<{ orderId: string }> = ({ orderId }) => {
  const { emails, isLoading } = useEmailsSentForOrder(orderId);
  if (isLoading) {
    return <LinearProgress />;
  }
  if (!emails || emails.length === 0) {
    return <Typography variant="body2">Brak wiadomości email do wyświetlenia</Typography>;
  }
  const sortedEmails = orderBy(emails, (e) => e.createdAt, "desc");
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Data i czas</TableCell>
          <TableCell>Kategoria</TableCell>
          <TableCell>Status</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedEmails.map((e) => (
          <EmailRow key={e.createdAt} email={e} />
        ))}
      </TableBody>
    </Table>
  );
};

export default EmailsSentForOrder;
